import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem } from '@mui/material';
import { setAffectedVehiclesValidations, setVehicleGroups, setVolumesAndOriginChanged } from '@/redux/Campaign/campaign.action';
import {
  getAffectedVehiclesValidations, getCampaignInfo, getCampaignValidations, getCampaignVehiclesVolumes, getLimitationPlants,
  getOriginalValues,
} from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import useToastContext from '@/hooks/useToastContext';
import { errorTypes } from '@/helpers/constants';
import { stringToDate } from '@/helpers/utils';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import { AffectedVehiclesOriginalValues, LimitationPlant, LimitationPlants, ModelSerie, VehicleGroup, Volume } from '@/types';
import AccordionGray from '@/components/UI/AccordionGray/AccordionGray';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import MultiSelection from '@/components/UI/MultiSelection/MultiSelection';
import VehicleCleanDirtInput from '@/components/UI/VehicleCleanDirtInput/VehicleCleanDirtInput';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import { getOriginValuesNewState } from '../volumeDetails-utils';
import { MENU, OPTIONS_MENU, VEHICLESNOK_DATA } from './VehicleCleanDirt-constants';
import {
  deleteItem, getInputValue, getMSNotSelected, getSelectedModelSeries, handleChangeDate, handleChangeInput, selectMenuChips, styleValidOnNok,
} from './VehicleCleanDirt-utils';
import styles from './ModelSeriesAccordions.module.scss';

interface DialogConfirmProps {
  open: boolean;
  groupToDelete: any;
}

interface ModelSeriesAccordionsProps {
  disabled: boolean;
  tabSelected: string;
  plantSelected?: LimitationPlant;
  volumes: Volume[];
  originalValues: AffectedVehiclesOriginalValues;
  affectedVehiclesValidations: any;
  setPlantVehicleGroups: (groupId: number, vehicleGroups: ModelSerie[], plantSelected?: LimitationPlant) => void;
  setPlantsValidations: (validations: any) => void;
  headerLanguage: string;
  validations: any;
  setVolumesOriginhadChanged: (changed: any) => void;
  limitationPlants: LimitationPlants;
  hasAllMSSelected: boolean;
}

const ModelSeriesAccordions: React.FC<ModelSeriesAccordionsProps> = (props) => {
  const { disabled, tabSelected, plantSelected, volumes, originalValues, affectedVehiclesValidations, setPlantVehicleGroups,
    setPlantsValidations, headerLanguage, validations, setVolumesOriginhadChanged, limitationPlants, hasAllMSSelected } = props;
  const [anchorElnOK, setAnchorElnOK] = useState({});
  const [anchorElOK, setAnchorElOK] = useState({});
  const [dialogConfirmProps, setDialogConfirmProps] = useState<DialogConfirmProps>({ open: false, groupToDelete: null });
  const { t } = useTranslation();
  const { detectLanguage, detectLanguageTranslation } = useDetectLanguage();
  const { addToast } = useToastContext();
  const vehiclesCleanDirt = Object.values(VEHICLESNOK_DATA).map(({ key, value }) => ({ key, value }));
  const limitation = volumes.find((v) => v.plant === plantSelected?.wkzNumber)?.limitationVolumeManualEntry;

  const handleMenuAction = (action, ev, section, groupId) => {
    const value = action === 'OPEN' ? ev.currentTarget : null;

    if (section === 'cleanPoint') {
      setAnchorElOK({
        ...anchorElOK,
        [groupId]: value,
      });
    } else {
      setAnchorElnOK({
        ...anchorElOK,
        [groupId]: value,
      });
    }
  };

  const getAnchorValues = (section: string, groupId: number) => (section === 'cleanPoint'
    ? {
      anchor: anchorElOK?.[groupId],
      items: MENU.OKRequest[0],
    }
    : {
      anchor: anchorElnOK?.[groupId],
      items: MENU.NOKRequest[0],
    });

  const getVolumeSelected = (groupId: number, label: string) => {
    const volumeFound = volumes?.find((currentVolume) => currentVolume?.plant === plantSelected?.wkzNumber);

    return volumeFound?.limitationVolumeManualEntry?.vehicleGroups?.[groupId]?.[label];
  };

  const deleteVehicles = (groupId: number) => (section: string, item: string, index: number) => {
    const optionsLabels = getVolumeSelected(groupId, section);
    const attribute = vehiclesCleanDirt.find(({ value }) => value === item)?.key ?? '';

    if (['dates', 'fromStartOfProductions', 'vehicleIdentificationNumbers', 'shiftNumbers',
      'productionNumbers', 'ttzs', 'otherDescriptions', 'vehicleRestrictionViaFileUpload', 'releases'].includes(attribute)) {
      deleteItem(groupId, attribute, index, optionsLabels, section, volumes, plantSelected, setVolumesOriginhadChanged,
        originalValues, headerLanguage, validations, setPlantsValidations, limitationPlants);
    }
  };

  const onChangeDate = (groupId: number) => (section: string, option: string, index: number, item: any) => {
    const optionsLabels = getVolumeSelected(groupId, section);
    const attribute = vehiclesCleanDirt.find(({ value }) => value === option)?.key ?? '';

    if (['dates', 'ttzs'].includes(attribute)) {
      handleChangeDate(groupId, attribute, optionsLabels, index, item, section, volumes, plantSelected,
        setVolumesOriginhadChanged, originalValues, headerLanguage, validations, setPlantsValidations, limitationPlants);
    }
  };

  const onChangeInput = (groupId: number) => (section: string, option: string, index: number, item: any, isTime: boolean, isDropdown: boolean,
    isAdditionalInput: boolean) => {
    const optionsLabels = getVolumeSelected(groupId, section);
    const attribute = vehiclesCleanDirt.find(({ value }) => value === option)?.key ?? '';

    if (['dates', 'vehicleIdentificationNumbers', 'shiftNumbers', 'productionNumbers', 'otherDescriptions', 'releases'].includes(attribute)) {
      handleChangeInput(groupId, attribute, optionsLabels, index, item, section, volumes, plantSelected,
        setVolumesOriginhadChanged, originalValues, headerLanguage, isTime, isDropdown, isAdditionalInput, validations,
        setPlantsValidations, limitationPlants);
      let label: string;

      if (attribute === 'otherDescriptions') {
        label = section === 'dirtPoint' ? 'otherDescriptionNok' : 'otherDescriptionOk';
      } else {
        label = attribute;
      }

      detectLanguageTranslation(label, plantSelected?.wkzNumber, groupId);
    }
  };

  const isInvalid = (groupId: number, key: string, section: string, index: number, field: string) => {
    const basePath = affectedVehiclesValidations?.[headerLanguage]?.volumes?.[plantSelected?.wkzNumber ?? '']?.vehiclesCleanDirt
      ?.[groupId]?.[section]?.[key]?.[index];

    if (key === 'dates' || key === 'releases') {
      return basePath?.[field]?.invalid;
    }

    return basePath?.invalid;
  };

  const isVehiclesSelectorInvalid = useMemo(() => {
    const invalid = affectedVehiclesValidations?.[headerLanguage]?.volumes?.[plantSelected?.wkzNumber ?? '']
      ?.allVehiclesInGroups?.invalid;
    if (invalid) addToast(t('alert.allVehiclesInGroups'), 'Warning', errorTypes.NOT_OKNOK_IN_GROUPS_ERROR);

    return invalid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affectedVehiclesValidations?.[headerLanguage]?.volumes?.[plantSelected?.wkzNumber ?? '']
    ?.allVehiclesInGroups, headerLanguage, plantSelected]);

  const getSelections = (selections: ModelSerie[]) => selections.map((s) => `${s.modelSeries}-${s.typeVersion}`);

  const getMSOptions = (currSelection: ModelSerie[]) => [...getMSNotSelected(volumes, limitationPlants, plantSelected), ...getSelections(currSelection)];

  const handleSelectMSChange = (event: any, groupId: number) => {
    const newValue = event.target.value;
    const newGroups = [...newValue].map((ms) => {
      const [mSeries, tVersion] = ms.split('-');

      return { modelSeries: mSeries, typeVersion: tVersion };
    });
    setPlantVehicleGroups(groupId, newGroups, plantSelected);
  };

  const handleMSChipDelete = (msDeleted: string, groupId: number) => {
    const [mSeries, tVersion] = msDeleted.split('-');
    const newGroups = limitation?.vehicleGroups?.[groupId]?.selections
      ?.filter(({ modelSeries, typeVersion }) => !(modelSeries === mSeries && typeVersion === tVersion)) ?? [];
    setPlantVehicleGroups(groupId, newGroups, plantSelected);
  };

  const handleDeleteGroup = (group: VehicleGroup, groupId: number, fromConfirmation: boolean) => {
    if (!fromConfirmation) {
      setDialogConfirmProps({
        open: true,
        groupToDelete: { group: group.selections.map(({ modelSeries, typeVersion }) => `${modelSeries}-${typeVersion}`), groupId },
      });

      return;
    }
    const newVolumes = volumes.map((vol) => (vol.plant === plantSelected?.wkzNumber
      ? {
        ...vol,
        limitationVolumeManualEntry: {
          ...vol.limitationVolumeManualEntry,
          vehicleGroups: vol.limitationVolumeManualEntry.vehicleGroups?.filter((vg, index) => (index !== groupId)),
        },
      }
      : vol));

    setVolumesOriginhadChanged({
      originalValues: getOriginValuesNewState(originalValues, plantSelected),
      volumes: newVolumes,
    });
    setDialogConfirmProps({ open: false, groupToDelete: null });
  };

  return (
    <>
      {limitation?.vehicleGroups?.map((group, groupId) => (
        group.selections.length === 0 && hasAllMSSelected
          ? null
          :
          (
            <AccordionGray
              key={`${groupId}-${plantSelected?.wkzNumber}`} // eslint-disable-line react/no-array-index-key
              id={`nok-ok-accordion-${groupId}-${plantSelected?.wkzNumber}`}
              title={getSelections(group.selections).join(', ')}
              onDelete={(disabled || groupId === 0) ? null : (() => handleDeleteGroup(group, groupId, false))}
              defaultExpanded={(groupId === 0 || groupId === limitation?.vehicleGroups?.length - 1)}
            >
              <MultiSelection
                id={`nok-ok-series-group-${groupId}-${plantSelected?.wkzNumber}`}
                key={`${groupId}-${plantSelected?.wkzNumber}`} // eslint-disable-line react/no-array-index-key
                label={t('vehicles')}
                disabled={disabled}
                options={getMSOptions(group.selections)}
                value={getSelections(group.selections)}
                isMandatory
                isModelSeries
                maxChip={6}
                formControlClass={styles.MultiSelection}
                handleChange={(ev) => handleSelectMSChange(ev, groupId)}
                handleDeleteItem={(msDeleted) => handleMSChipDelete(msDeleted, groupId)}
                invalid={groupId === 0 && isVehiclesSelectorInvalid}
                fieldPath={['affectedVehicles', 'limitationVolumeManualEntry', `vehicleGroups[${groupId}]`, 'selections']}
              />
              <div className={styles.NOkOk}>
                {Object.keys(OPTIONS_MENU).map((section, sectionIndex) => (
                  <section key={`${tabSelected}-${section}`}>
                    {disabled
                      ? <span className={styles.AddCriteria}>{t(`VehicleCleanDirt.${section}Criterias`)}</span>
                      : (
                        <div className={styles.NOk}>
                          <IcoButtonSimple
                            id={`add-button-${tabSelected}-${sectionIndex}-${groupId}`}
                            icon="icon-plus-hinzufuegen-klein"
                            disabled={disabled}
                            tooltipText={t(`VehicleCleanDirt.add${section}Criteria`)}
                            handleClick={(ev) => handleMenuAction('OPEN', ev, section, groupId)}
                          />
                          <span
                            className={`${styles.AddCriteria} ${styleValidOnNok(section, tabSelected, affectedVehiclesValidations,
                              headerLanguage, plantSelected, validations)}`}
                          >{t(`VehicleCleanDirt.add${section}Criteria`)}
                          </span>
                          <Menu
                            id={`menu-${tabSelected}-${sectionIndex}-${groupId}`}
                            key={`menu-${tabSelected}-${sectionIndex}-${groupId}`} // eslint-disable-line react/no-array-index-key
                            anchorEl={getAnchorValues(section, groupId)?.anchor}
                            keepMounted
                            open={Boolean(getAnchorValues(section, groupId)?.anchor)}
                            onClose={() => handleMenuAction('CLOSE', null, section, groupId)}
                          >
                            {Boolean(getAnchorValues(section, groupId)?.anchor)
                              && Object.values(getAnchorValues(section, groupId)?.items).map((itemMenu) => (
                                <MenuItem
                                  id={`menu-option-${tabSelected}-${sectionIndex}-${itemMenu.key.replace(/\s/g, '-')}`}
                                  key={`${section}-${itemMenu.key}-${groupId}`} // eslint-disable-line react/no-array-index-key
                                  onClick={() => selectMenuChips(itemMenu.key, section, groupId, group[section], vehiclesCleanDirt,
                                    headerLanguage, volumes, plantSelected, setVolumesOriginhadChanged, originalValues,
                                    handleMenuAction, validations, setPlantsValidations, limitationPlants)}
                                >
                                  {t(itemMenu.label)}
                                </MenuItem>
                              ))}
                          </Menu>
                        </div>
                      )}
                    <div className={styles.Entries}>
                      {Object.values(OPTIONS_MENU[section])?.map((op: any, i: number) => (
                        group[section]?.[op.key]?.map((option, index) => (
                          <VehicleCleanDirtInput
                            key={`${section}-${option.key}`}
                            optionKey={op.key}
                            checkEnabled={disabled}
                            fieldPath={['affectedVehicles', 'limitationVolumeManualEntry', `vehicleGroups[${groupId}]`, section, op.key]}
                            groupId={groupId}
                            chipProps={{
                              show: op.chipProps.show,
                              code: op.value,
                              key: `${section}-${option}-${index}-${groupId}`,
                              label: t(op.chipProps.label),
                              index,
                              onDelete: deleteVehicles(groupId),
                              section,
                            }}
                            calendarProps={{
                              show: op.calendarProps.show,
                              code: op.value,
                              id: `${op.calendarProps.id}${option}`,
                              key: getVolumeSelected(groupId, section)?.[op.key]?.[index]?.calendarDate,
                              label: t(op.calendarProps.label),
                              index,
                              value: getInputValue(op.key, index, true, group[section], stringToDate, headerLanguage),
                              onChange: onChangeDate(groupId),
                              section,
                              invalid: isInvalid(groupId, op.key, section, index, 'textDate'),
                            }}
                            inputProps={{
                              show: op.inputProps.show,
                              code: op.value,
                              id: op.inputProps.id,
                              key: `${op.inputProps.key}-${i}-${index}`,
                              label: t(op.inputProps.label),
                              index,
                              value: getInputValue(op.key, index, false, group[section], stringToDate, headerLanguage),
                              onChange: onChangeInput(groupId),
                              detectLanguage,
                              section,
                              invalid: isInvalid(groupId, op.key, section, index, 'referenceDate'),
                              isOptional: op.inputProps?.isOptional,
                              fullWidth: op.inputProps?.fullWidth,
                              charLimit: op.inputProps?.charLimit,
                            }}
                            additionalInputProps={{
                              show: op.additionalInputProps.show,
                              code: op.value,
                              id: op.additionalInputProps.id,
                              key: `${op.additionalInputProps.key}-${i}-${index}`,
                              label: t(op.additionalInputProps.label),
                              index,
                              value: getInputValue(op.key, index, false, group[section], stringToDate, headerLanguage, false, false, true),
                              onChange: onChangeInput(groupId),
                              detectLanguage,
                              section,
                              invalid: isInvalid(groupId, op.key, section, index, 'referenceDate'),
                              isOptional: op.additionalInputProps?.isOptional,
                              fullWidth: op.additionalInputProps?.fullWidth,
                              charLimit: op.additionalInputProps?.charLimit,
                            }}
                            inputPropsTime={{
                              show: op.inputPropsTime.show,
                              code: op.value,
                              id: op.inputPropsTime.id,
                              type: op.inputPropsTime.type,
                              key: `${op.inputPropsTime.key}-${i}-${index}`,
                              label: t(op.inputPropsTime.label),
                              index,
                              value: getInputValue(op.key, index, true, group[section], stringToDate, headerLanguage, true),
                              onChange: onChangeInput(groupId),
                              section,
                              invalid: isInvalid(groupId, op.key, section, index, 'time'),
                            }}
                            drowpdownProps={{
                              show: op.drowpdownProps.show,
                              code: op.value,
                              id: op.drowpdownProps.id,
                              key: `${op.drowpdownProps.key}-${i}-${index}`,
                              label: t(op.drowpdownProps.label),
                              form: op.drowpdownProps.form,
                              index,
                              value: getInputValue(op.key, index, false, group[section], stringToDate, headerLanguage, false, true),
                              onChange: onChangeInput(groupId),
                              section,
                              invalid: isInvalid(groupId, op.key, section, index, 'moreUpcomingReleases'),
                            }}
                          />
                        ))
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </AccordionGray>
          )
      ))}

      {dialogConfirmProps.open && (
        <ConfirmDialog
          close={() => setDialogConfirmProps({ open: false, groupToDelete: null })}
          confirm={() => handleDeleteGroup(dialogConfirmProps?.groupToDelete?.group, dialogConfirmProps?.groupToDelete?.groupId, true)}
          title={t('dialog.deleteVehicleGroup.title')}
          message={dialogConfirmProps?.groupToDelete?.group?.length > 0
            ? (
              <>
                <span>
                  {t('dialog.deleteVehicleGroup.messageWithSelecteds')}<b>{dialogConfirmProps?.groupToDelete.group.join(', ')}</b>
                </span>
                <p>{t('dialog.deleteVehicleGroup.message')}</p>
              </>
            )
            : t('dialog.deleteVehicleGroup.message')}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.confirm')}
        />
      )}
    </>
  );
};

const actionCreators = {
  setPlantsValidations: setAffectedVehiclesValidations,
  setVolumesOriginhadChanged: setVolumesAndOriginChanged,
  setPlantVehicleGroups: setVehicleGroups,
};

const mapStateToProps = (state) => ({
  volumes: getCampaignVehiclesVolumes(state),
  originalValues: getOriginalValues(state),
  affectedVehiclesValidations: getAffectedVehiclesValidations(state),
  validations: getCampaignValidations(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  limitationPlants: getLimitationPlants(state),
});

export default connect(mapStateToProps, actionCreators)(ModelSeriesAccordions);
